import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboardingController, useTourController } from '../controller';
import { isLiveInputPath } from '../model/liveGuard';
import { useInterfaceStore } from '@/store/useInterfaceStore';
import { GETTING_STARTED_TUTORIAL_ID, trackTutorialCompleted } from '@/features/tutorials/model/progress';
import { OnboardingTooltip } from './OnboardingTooltip';
import { SpotlightLayer } from './SpotlightLayer';
import { useSpotlightTracker } from './useSpotlightTracker';
import { canHover } from './viewport';

const READY_DELAY_MS = 100;

export const OnboardingOverlay = () => {
  const {
    state,
    currentStep,
    allSteps,
    isVisible,
    spotlightPosition,
    hoverSection,
    completeStep,
    nextStep,
    previousStep,
    skip,
    complete,
    pause,
    setSpotlightPosition,
  } = useOnboardingController();
  const isTourActive = useTourController((s) => s.activeTour !== null);
  const { t } = useTranslation('onboarding');

  const setSidebarOpen = useInterfaceStore((s) => s.setSidebarOpen);
  const sidebar = useInterfaceStore((s) => s.sidebar);
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  const requiresNavigation = useMemo(() => {
    return Boolean(
      currentStep?.requiresAction &&
        currentStep?.actionType === 'navigate' &&
        currentStep?.actionTarget,
    );
  }, [currentStep]);

  const isOnRequiredRoute = useMemo(() => {
    if (!requiresNavigation || !currentStep?.actionTarget) return true;
    return location.pathname.startsWith(currentStep.actionTarget);
  }, [requiresNavigation, currentStep?.actionTarget, location.pathname]);

  const { targetElement, elementFound } = useSpotlightTracker({
    currentStep,
    // A running tour owns the spotlight: this tracker must not open the sidebar meanwhile.
    isVisible: isVisible && !isTourActive && !isLiveInputPath(location.pathname),
    isReady,
    setSpotlightPosition,
  });

  useEffect(() => {
    if (!isVisible) {
      setIsReady(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsReady(true);
    }, READY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !currentStep || !targetElement) return;

    const handleHover = () => {
      if (currentStep.requiredHover) {
        hoverSection(currentStep.id);
      }
    };

    targetElement.addEventListener('mouseenter', handleHover);
    targetElement.addEventListener('focus', handleHover, true);

    return () => {
      targetElement.removeEventListener('mouseenter', handleHover);
      targetElement.removeEventListener('focus', handleHover, true);
    };
  }, [isVisible, currentStep, targetElement, hoverSection]);

  const handleNext = useCallback(() => {
    if (currentStep) {
      completeStep(currentStep.id);
      if (!elementFound || (requiresNavigation && isOnRequiredRoute) || (currentStep.requiredHover && !canHover())) {
        hoverSection(currentStep.id);
      }
    }
    nextStep();
  }, [
    currentStep,
    completeStep,
    elementFound,
    requiresNavigation,
    isOnRequiredRoute,
    hoverSection,
    nextStep,
  ]);

  const handleComplete = useCallback(async () => {
    if (currentStep) {
      await completeStep(currentStep.id);
      if (!elementFound || (requiresNavigation && isOnRequiredRoute) || (currentStep.requiredHover && !canHover())) {
        hoverSection(currentStep.id);
      }
    }
    await complete();
    trackTutorialCompleted(GETTING_STARTED_TUTORIAL_ID, 'tour');
    if (!sidebar.isDesktop) {
      setSidebarOpen(false);
    }
  }, [
    currentStep,
    completeStep,
    elementFound,
    requiresNavigation,
    isOnRequiredRoute,
    hoverSection,
    complete,
    sidebar.isDesktop,
    setSidebarOpen,
  ]);

  const handleSkip = useCallback(async () => {
    await skip();
    if (!sidebar.isDesktop) {
      setSidebarOpen(false);
    }
  }, [skip, sidebar.isDesktop, setSidebarOpen]);

  if (!isVisible || !currentStep || !state || !isReady || isTourActive) return null;
  if (isLiveInputPath(location.pathname)) return null;

  const currentIndex = allSteps.findIndex((s) => s.id === currentStep.id);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === allSteps.length - 1;
  const isHovered = state.hoveredSections.includes(currentStep.id);

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] pointer-events-none"
      aria-modal="true"
      role="dialog"
      aria-label={t(currentStep.titleKey)}
    >
      <SpotlightLayer
        position={spotlightPosition}
        targetElement={targetElement}
        label={t(currentStep.titleKey)}
        onTargetHover={() => {
          if (currentStep.requiredHover) {
            hoverSection(currentStep.id);
          }
        }}
      />

      <div className="pointer-events-auto">
        <OnboardingTooltip
          step={currentStep}
          position={spotlightPosition}
          onNext={handleNext}
          onPrev={previousStep}
          onSkip={handleSkip}
          onPause={pause}
          onComplete={handleComplete}
          isFirst={isFirst}
          isLast={isLast}
          isHovered={isHovered}
          currentIndex={currentIndex}
          totalSteps={allSteps.length}
          elementFound={elementFound}
          requiresNavigation={requiresNavigation}
          isOnRequiredRoute={isOnRequiredRoute}
        />
      </div>
    </div>,
    document.body,
  );
};
